/**
 * Set di caratteri SEPA (EPC / ISO 20022 Latin character set).
 *
 * Le banche rifiutano una disposizione il cui nome del beneficiario o la cui
 * causale contengano caratteri fuori da questo set — «Incorrect creditor name.
 * only AZ az 09 ?:()./,'+- e spazio». Il provider non sanifica nulla: la
 * pulizia è nostra e avviene SOLO al confine con il provider. Quello che
 * l'utente ha scritto resta salvato in Previxio così com'è.
 *
 * Ammessi: A-Z, a-z, 0-9, spazio e i segni `/ - ? : ( ) . , ' +`.
 */
const SEPA_ALLOWED_CHARACTER_CLASS = "A-Za-z0-9 /?:().,'+\\-";
/** Lunghezza massima SEPA del nome di una parte (creditore o debitore). */
export const SEPA_NAME_MAX_LENGTH = 70;
/** Lunghezza massima SEPA della causale (remittance information). */
export const SEPA_REMITTANCE_MAX_LENGTH = 140;
/**
 * Vera per una stringa interamente dentro il set ammesso (vuota compresa).
 * È l'invariante che l'output di `toSepaText` soddisfa sempre.
 */
export const SEPA_ALLOWED_CHARACTER_REGEX = new RegExp(`^[${SEPA_ALLOWED_CHARACTER_CLASS}]*$`);
const DISALLOWED_CHARACTER_REGEX = new RegExp(`[^${SEPA_ALLOWED_CHARACTER_CLASS}]`, 'g');
/**
 * Lettere latine che `normalize('NFD')` NON scompone, perché non sono una
 * base più un segno: vanno traslitterate a mano o finirebbero sostituite da
 * uno spazio, mutilando il nome.
 */
const TRANSLITERATIONS = {
    ß: 'ss',
    æ: 'ae',
    Æ: 'AE',
    œ: 'oe',
    Œ: 'OE',
    ø: 'o',
    Ø: 'O',
    đ: 'd',
    Đ: 'D',
    ł: 'l',
    Ł: 'L',
    þ: 'th',
    Þ: 'TH',
    ð: 'd',
    Ð: 'D',
};
const TRANSLITERABLE_REGEX = new RegExp(`[${Object.keys(TRANSLITERATIONS).join('')}]`, 'g');
/** Segni diacritici combinanti prodotti dalla decomposizione NFD. */
const COMBINING_MARKS_REGEX = /[\u0300-\u036F]/g;
const AMPERSAND_REGEX = /&/g;
const LOWERCASE_LETTER_REGEX = /[a-z]/;
const AMPERSAND_AS_LOWERCASE = ' e ';
const AMPERSAND_AS_UPPERCASE = ' E ';
const QUOTE_REGEX = /["“”‘’´`]/g;
const STRAIGHT_APOSTROPHE = "'";
const LONG_DASH_REGEX = /[–—]/g;
const HYPHEN = '-';
const UNDERSCORE_REGEX = /_/g;
const SEMICOLON_REGEX = /;/g;
const EXCLAMATION_MARK_REGEX = /!/g;
const MULTIPLE_WHITESPACE_REGEX = /\s+/g;
const SPACE = ' ';
const COMMA = ',';
const PERIOD = '.';
/**
 * Sostituzioni che CONSERVANO il significato del segno invece di cancellarlo.
 * L'ampersand non è qui perché la sua resa dipende dalla stringa intera.
 */
const SEMANTIC_REPLACEMENTS = [
    [QUOTE_REGEX, STRAIGHT_APOSTROPHE],
    [LONG_DASH_REGEX, HYPHEN],
    [UNDERSCORE_REGEX, SPACE],
    [SEMICOLON_REGEX, COMMA],
    [EXCLAMATION_MARK_REGEX, PERIOD],
];
const transliterate = (value) => value.replace(TRANSLITERABLE_REGEX, (character) => TRANSLITERATIONS[character] ?? character);
const removeDiacritics = (value) => value.normalize('NFD').replace(COMBINING_MARKS_REGEX, '');
/**
 * «Rossi&Figli» → «Rossi e Figli», «ROSSI & FIGLI» → «ROSSI E FIGLI»: la
 * congiunzione segue il caso della stringa, e gli spazi in eccesso li toglie
 * il collasso finale.
 */
const replaceAmpersand = (value) => value.replace(AMPERSAND_REGEX, LOWERCASE_LETTER_REGEX.test(value)
    ? AMPERSAND_AS_LOWERCASE
    : AMPERSAND_AS_UPPERCASE);
const applySemanticReplacements = (value) => SEMANTIC_REPLACEMENTS.reduce((current, [pattern, replacement]) => current.replace(pattern, replacement), replaceAmpersand(value));
/**
 * Riscrive un testo nel set di caratteri SEPA, senza troncarlo.
 *
 * Nell'ordine: traslitterazione delle lettere latine non scomponibili,
 * rimozione degli accenti, sostituzioni che conservano il significato del
 * segno, tutto il resto fuori dal set → spazio, collasso degli spazi e trim.
 *
 * Input assente o vuoto → stringa vuota.
 */
export function toSepaText(value) {
    if (!value)
        return '';
    return applySemanticReplacements(removeDiacritics(transliterate(value)))
        .replace(DISALLOWED_CHARACTER_REGEX, SPACE)
        .replace(MULTIPLE_WHITESPACE_REGEX, SPACE)
        .trim();
}
const truncateToSepaLength = (value, maxLength) => value.slice(0, maxLength).trimEnd();
/** Nome di una parte SEPA: testo sanificato e troncato a 70 caratteri. */
export const toSepaName = (value) => truncateToSepaLength(toSepaText(value), SEPA_NAME_MAX_LENGTH);
/** Causale SEPA: testo sanificato e troncato a 140 caratteri. */
export const toSepaRemittance = (value) => truncateToSepaLength(toSepaText(value), SEPA_REMITTANCE_MAX_LENGTH);
//# sourceMappingURL=sepa-text.js.map